import "./style.scss";

import { PortalType } from "contexts/portal.context";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";
import { fetchChannels } from "store/channelStore";
import { patchCompanyInfo } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { EPortalType } from "store/types";

import BlockModal from "../BlockModal";
import ChipsAutocomplete from "../ChipsAutocomplete";
import InsertVariable from "../InsertVariableMenu";
import SaveButton from "../SaveButton";
import SettingBoolean from "../SettingBoolean";
import StyledTextField from "../StyledTextField/StyledTextField";
import TitleSeparation from "../TitleSeparation";
import { getChannelIdsFromNames, getChannelSelectorOptions, getSavedChannelSettings } from "./helpers";

type TProps = {
    hasDataSettings?: boolean;
};

const getChannelSelectorSaveValue = (
    enabled: boolean,
    selectedChannels: Array<{ id: number }>,
    totalChannelsCount: number,
): number | string => {
    let value: boolean | number[];

    if (!enabled) {
        value = false;
    } else if (selectedChannels.length === 0 || selectedChannels.length === totalChannelsCount) {
        value = true;
    } else {
        value = selectedChannels.map((ch) => ch.id);
    }

    return typeof value === "boolean" ? +value : JSON.stringify(value);
};

const StatementSettings = ({ hasDataSettings = false }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const portalType = useContext(PortalType);

    const [isEdited, setIsEdited] = useState(false);
    const blocker = useBlocker(isEdited);

    const { getCompanySettings, companyInfos, countryFeatures } = useCompanySettings();

    const usesAdvanceCompensation = getCompanySettings("uses_interterm_compensation");

    const [showsContribution, setShowsContribution] = useState(
        getCompanySettings("net_statement_show_contributor_cost"),
    );
    const [showsNIR, setShowsNIR] = useState(getCompanySettings("uses_statement_social_security_number"));
    const [showStockMovements, setShowStockMovements] = useState(companyInfos?.showStockMovements);
    const [showStatementNumbering, setShowStatementNumbering] = useState(
        getCompanySettings("uses_statement_numbering"),
    );
    const [hideZeroLine, setHideZeroLine] = useState(getCompanySettings("hide_zero_lines_on_statement"));
    const [hideZeroPercentTerms, setHideZeroPercentTerms] = useState(
        getCompanySettings("hide_zero_percent_terms_on_statement"),
    );
    const [hideZeroSalesChannels, setHideZeroSalesChannels] = useState(getCompanySettings("hide_zero_sales_channels"));
    const [showZeroPercent, setShowZeroPercent] = useState(getCompanySettings("show_zero_percent_on_statement"));
    const [statementTemplate, setStatementTemplate] = useState(getCompanySettings("statement_numbering_template"));
    const [showSoldQuantity, setShowSoldQuantity] = useState(
        getCompanySettings("show_sold_quantity_in_consolidated_statement"),
    );
    const [showTitleBalance, setShowTitleBalance] = useState(
        getCompanySettings("show_balance_in_consolidated_statement"),
    );
    const [showBalanceUpdateDetails, setShowBalanceUpdateDetails] = useState(
        getCompanySettings("show_balance_update_on_statement"),
    );
    const [showProvisionLines, setShowProvisionLines] = useState(
        getCompanySettings("show_provision_lines_on_statement"),
    );
    const [showEan13, setShowEan13] = useState(getCompanySettings("show_ean13_on_statement"));
    const [showReleaseDate, setShowReleaseDate] = useState(getCompanySettings("show_release_date_on_statement"));
    const [showSKU, setShowSKU] = useState(getCompanySettings("show_sku_on_statement"));
    const [showTermChannelPercentage, setShowTermChannelPercentage] = useState(
        getCompanySettings("show_term_channel_percentage_on_statement"),
    );
    const [showCompensationGroup, setShowCompensationGroup] = useState(
        getCompanySettings("show_compensation_group_on_statement"),
    );
    const [hideGrossRightsTTC, setHideGrossRightsTTC] = useState(
        getCompanySettings("hide_gross_rights_ttc_on_consolidated_statement"),
    );
    const savedShowAnnexeAmountOrQty = getCompanySettings("show_amount_or_qty_on_statement_annexe");
    const [showAnnexeAmountOrQty, setShowAnnexeAmountOrQty] = useState(
        savedShowAnnexeAmountOrQty === null ? true : !!savedShowAnnexeAmountOrQty,
    );

    const showPeriodRevenueChannelsFromSettings = getCompanySettings("show_channel_period_revenue_on_statement");
    const showTitlePriceChannelsFromSettings = getCompanySettings("show_channel_title_price_on_statement");

    const [showTitlePriceEnabled, setShowTitlePriceEnabled] = useState(() => {
        const savedValue = getCompanySettings("show_channel_title_price_on_statement");
        return (
            savedValue === true ||
            (Array.isArray(savedValue) && savedValue.length > 0) ||
            (typeof savedValue === "string" && savedValue !== "false")
        );
    });

    const [showTitlePriceChannels, setShowTitlePriceChannels] = useState<
        Array<{
            id: number;
            name: string;
            color: number;
        }>
    >([]);

    const [showPeriodRevenueEnabled, setShowPeriodRevenueEnabled] = useState(() => {
        const savedValue = getCompanySettings("show_channel_period_revenue_on_statement");
        return (
            savedValue === true ||
            (Array.isArray(savedValue) && savedValue.length > 0) ||
            (typeof savedValue === "string" && savedValue !== "false")
        );
    });

    const [showPeriodRevenueChannels, setShowPeriodRevenueChannels] = useState<
        Array<{
            id: number;
            name: string;
            color: number;
        }>
    >([]);

    const { channels } = useAppSelector((state) => state.channels);

    useEffect(() => {
        if (channels.length > 0) {
            const savedIds = getSavedChannelSettings(showTitlePriceChannelsFromSettings);
            const selectorOptions = getChannelSelectorOptions(savedIds, channels);
            setShowTitlePriceChannels(selectorOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channels]);

    useEffect(() => {
        if (channels.length > 0) {
            const savedIds = getSavedChannelSettings(showPeriodRevenueChannelsFromSettings);
            const selectorOptions = getChannelSelectorOptions(savedIds, channels);
            setShowPeriodRevenueChannels(selectorOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channels]);

    useEffect(() => {
        dispatch(fetchChannels());
    }, [dispatch]);

    const inputEl = useRef<HTMLInputElement>(null);
    const [cursorPosition, setCursorPosition] = useState(statementTemplate?.length);

    const variables = {
        "{{year}}": t("CompanySettings.VariableYear"),
        "{{month}}": t("CompanySettings.VariableMonth"),
        "{{day}}": t("CompanySettings.VariableDay"),
        "{{number}}": t("CompanySettings.VariableNumber"),
    };

    const handleSelectVariable = (newValue: string) => {
        setStatementTemplate(newValue);
        setCursorPosition(newValue.length);
    };

    const handleShowTitlePriceToggle = (value: boolean) => {
        setShowTitlePriceEnabled(value);
        if (!value) {
            setShowTitlePriceChannels([]);
        }
        setIsEdited(true);
    };

    const handleShowPeriodRevenueToggle = (value: boolean) => {
        setShowPeriodRevenueEnabled(value);
        if (!value) {
            setShowPeriodRevenueChannels([]);
        }
        setIsEdited(true);
    };

    // Configuration for settings with conditional selectors
    const conditionalSettings = [
        {
            name: "showTitlePrice",
            title: t("CompanySettings.ShowTitlePrice"),
            description: t("CompanySettings.ShowTitlePriceDesc"),
            enabled: showTitlePriceEnabled,
            handleToggle: handleShowTitlePriceToggle,
            shown: portalType === EPortalType.BOOK,
            renderSelector: () => (
                <ChipsAutocomplete
                    options={channels}
                    label={t("CompanySettings.SelectChannels")}
                    noOptionsText={t("CompanySettings.NoChannelsAvailable")}
                    filter="channels"
                    chosenTags={showTitlePriceChannels}
                    setChosenTags={(channelNames: string[] | ((prevState: string[]) => string[])) => {
                        if (Array.isArray(channelNames)) {
                            const channelObjects = getChannelSelectorOptions(
                                getChannelIdsFromNames(channels, channelNames),
                                channels,
                            );
                            setShowTitlePriceChannels(channelObjects);
                            setIsEdited(true);
                        }
                    }}
                />
            ),
        },
        {
            name: "showPeriodRevenue",
            title: t("CompanySettings.ShowPeriodRevenue"),
            description: t("CompanySettings.ShowPeriodRevenueDesc"),
            enabled: showPeriodRevenueEnabled,
            handleToggle: handleShowPeriodRevenueToggle,
            shown: portalType === EPortalType.BOOK,
            renderSelector: () => (
                <ChipsAutocomplete
                    options={channels}
                    label={t("CompanySettings.SelectChannels")}
                    noOptionsText={t("CompanySettings.NoChannelsAvailable")}
                    filter="channels"
                    chosenTags={showPeriodRevenueChannels}
                    setChosenTags={(channelNames: string[] | ((prevState: string[]) => string[])) => {
                        if (Array.isArray(channelNames)) {
                            const channelObjects = getChannelSelectorOptions(
                                getChannelIdsFromNames(channels, channelNames),
                                channels,
                            );
                            setShowPeriodRevenueChannels(channelObjects);
                            setIsEdited(true);
                        }
                    }}
                />
            ),
        },
    ];

    const statementPreferences = [
        {
            name: "contribution",
            sttgName: "net_statement_show_contributor_cost",
            title: t("CompanySettings.ContributionTitle"),
            description: t("CompanySettings.ContributionStatement"),
            value: showsContribution,
            handleChange: setShowsContribution,
            shown: true,
        },
        {
            name: "nir",
            sttgName: "uses_statement_social_security_number",
            title: t("CompanySettings.NIRTitle"),
            description: t("CompanySettings.NIRStatement"),
            value: showsNIR,
            handleChange: setShowsNIR,
            shown: true,
        },
        {
            name: "showStockMovements",
            title: t("CompanySettings.StockMovementsTitle"),
            description: t("Stock.ShowMovement"),
            value: showStockMovements,
            handleChange: setShowStockMovements,
            shown: portalType === "book",
        },
        {
            name: "showStatementNumbering",
            sttgName: "uses_statement_numbering",
            title: t("CompanySettings.StatementNumbering"),
            description: t("CompanySettings.ShowStatementNumbering"),
            value: showStatementNumbering,
            handleChange: setShowStatementNumbering,
            shown: ["audiovisual", "press"].includes(portalType),
        },
        {
            name: "showZeroPercent",
            sttgName: "show_zero_percent_on_statement",
            title: t("CompanySettings.ZeroPercentChannels"),
            description: t("CompanySettings.ZeroPercentChannelsDescription"),
            value: showZeroPercent,
            handleChange: setShowZeroPercent,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "showSoldQuantity",
            sttgName: "show_sold_quantity_in_consolidated_statement",
            title: t("CompanySettings.ShowSoldQuantity"),
            description: t("CompanySettings.ShowSoldQuantityDesc"),
            value: showSoldQuantity,
            handleChange: setShowSoldQuantity,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "showBalanceConsolidated",
            sttgName: "show_balance_in_consolidated_statement",
            title: t("CompanySettings.ShowTitleBalance"),
            description: t("CompanySettings.ShowTitleBalanceDesc"),
            value: showTitleBalance,
            handleChange: setShowTitleBalance,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "hideZeroLines",
            sttgName: "hide_zero_lines_on_statement",
            title: t("CompanySettings.HideZeroLine"),
            description: usesAdvanceCompensation
                ? t("CompanySettings.HideZeroLineDesc_collateral")
                : t("CompanySettings.HideZeroLineDesc"),
            value: hideZeroLine,
            handleChange: setHideZeroLine,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "hideZeroPercentTerms",
            sttgName: "hide_zero_percent_terms_on_statement",
            title: t("CompanySettings.HideZeroPercentTerms"),
            description: t("CompanySettings.HideZeroPercentTermsDesc"),
            value: hideZeroPercentTerms,
            handleChange: setHideZeroPercentTerms,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "showBalanceUpdateDetails",
            sttgName: "show_balance_update_on_statement",
            title: t("CompanySettings.ShowBalanceUpdateDetails"),
            description: t("CompanySettings.ShowBalanceUpdateDetailsDesc"),
            value: showBalanceUpdateDetails,
            handleChange: setShowBalanceUpdateDetails,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "showProvisionLines",
            sttgName: "show_provision_lines_on_statement",
            title: t("CompanySettings.ShowProvisionLines"),
            description: t("CompanySettings.ShowProvisionLinesDesc"),
            value: showProvisionLines,
            handleChange: setShowProvisionLines,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "hideZeroSalesChannels",
            sttgName: "hide_zero_sales_channels",
            title: t("CompanySettings.HideZeroSalesChannels"),
            description: t("CompanySettings.HideZeroSalesChannelsDesc"),
            value: hideZeroSalesChannels,
            handleChange: setHideZeroSalesChannels,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "showEan13OnStatement",
            sttgName: "show_ean13_on_statement",
            title: t("CompanySettings.ShowEan13OnStatement"),
            description: t("CompanySettings.ShowEan13OnStatementDesc"),
            value: showEan13,
            handleChange: setShowEan13,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "showReleaseDateOnStatement",
            sttgName: "show_release_date_on_statement",
            title: t("CompanySettings.ShowReleaseDateOnStatement"),
            description: t("CompanySettings.ShowReleaseDateOnStatementDesc"),
            value: showReleaseDate,
            handleChange: setShowReleaseDate,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "showSKUOnStatement",
            sttgName: "show_sku_on_statement",
            title: t("CompanySettings.ShowSKU"),
            description: t("CompanySettings.ShowSKUDesc"),
            value: showSKU,
            handleChange: setShowSKU,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "showTermChannelPercentageOnStatement",
            sttgName: "show_term_channel_percentage_on_statement",
            title: t("CompanySettings.ShowTermChannelPercentage"),
            description: t("CompanySettings.ShowTermChannelPercentageDesc"),
            value: showTermChannelPercentage,
            handleChange: setShowTermChannelPercentage,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "showCompensationGroupOnStatement",
            sttgName: "show_compensation_group_on_statement",
            title: t("CompanySettings.ShowCompensationGroup"),
            description: t("CompanySettings.ShowCompensationGroupDesc"),
            value: showCompensationGroup,
            handleChange: setShowCompensationGroup,
            shown: portalType === EPortalType.BOOK,
        },
        {
            name: "hideGrossRightsTTC",
            sttgName: "hide_gross_rights_ttc_on_consolidated_statement",
            title: t("CompanySettings.HideGrossRightsTTC"),
            description: t("CompanySettings.HideGrossRightsTTCDesc"),
            value: hideGrossRightsTTC,
            handleChange: setHideGrossRightsTTC,
            shown: portalType === EPortalType.BOOK && countryFeatures.vat.options.length > 0,
        },
        {
            name: "showAnnexeAmountOrQty",
            sttgName: "show_amount_or_qty_on_statement_annexe",
            title: t("CompanySettings.ShowAnnexeAmountOrQty"),
            description: t("CompanySettings.ShowAnnexeAmountOrQtyDesc"),
            value: showAnnexeAmountOrQty,
            handleChange: setShowAnnexeAmountOrQty,
            shown: portalType === EPortalType.BOOK,
        },
    ];

    const handleSavingSettings = () => {
        const settings: { name: string; content: number | string }[] = statementPreferences
            .filter((setting) => !!setting.sttgName)
            .map((setting) => ({ name: setting.sttgName!, content: +setting.value }));

        // Crealo Press setting
        if (companyInfos?.settings.find((sttg) => sttg.name === "statement_numbering_template")) {
            settings.push({ name: "statement_numbering_template", content: statementTemplate });
        }

        settings.push({
            name: "show_channel_title_price_on_statement",
            content: getChannelSelectorSaveValue(showTitlePriceEnabled, showTitlePriceChannels, channels.length),
        });

        settings.push({
            name: "show_channel_period_revenue_on_statement",
            content: getChannelSelectorSaveValue(showPeriodRevenueEnabled, showPeriodRevenueChannels, channels.length),
        });

        dispatch(
            // Only send the fields this page manages.
            patchCompanyInfo({
                id: companyInfos?.id,
                name: companyInfos?.name,
                showStockMovements: typeof showStockMovements === "boolean" ? +showStockMovements : showStockMovements,
                settings: settings,
            }),
        ).then(() => {
            setIsEdited(false);
            blocker.reset?.();
        });
    };

    return (
        <div className="statement-settings">
            <BlockModal blocker={blocker} />
            <div className="statement-settings-header">
                <h1>{t("Settings.Statements")}</h1>
            </div>

            <div className="statement-settings-content">
                <div className="statement-display">
                    <TitleSeparation title={t("CompanySettings.Display")} />
                    {statementPreferences.map((option) => (
                        <>
                            {option.shown && (
                                <SettingBoolean
                                    name={option.name}
                                    title={option.title}
                                    description={option.description}
                                    value={option.value}
                                    handleChange={option.handleChange}
                                    setIsEdited={setIsEdited}
                                />
                            )}
                        </>
                    ))}

                    {conditionalSettings.map((setting) => (
                        <>
                            {setting.shown && (
                                <div key={setting.name} className="statement-channel-selector-setting">
                                    <SettingBoolean
                                        name={setting.name}
                                        title={setting.title}
                                        description={setting.description}
                                        value={setting.enabled}
                                        handleChange={setting.handleToggle}
                                        setIsEdited={setIsEdited}
                                    />
                                    {setting.enabled && (
                                        <div className="statement-channel-selector-dropdown">
                                            {setting.renderSelector()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    ))}
                </div>

                {hasDataSettings && (
                    <div className="statement-formatting">
                        <TitleSeparation title={t("CompanySettings.Format")} />
                        {/* <span className="section-subtitle">{t("CompanySettings.FormattingSubtitle")}</span> */}
                        <div className="statement-data">
                            {["audiovisual", "press"].includes(portalType) && showStatementNumbering && (
                                <div className="template-reference">
                                    <div className="template-top">
                                        <span className="label">{t("CompanySettings.FormatStatementNumbering")}</span>
                                        <InsertVariable
                                            cursorPosition={cursorPosition}
                                            field={statementTemplate && statementTemplate}
                                            variables={variables}
                                            onSelect={handleSelectVariable}
                                        />
                                    </div>
                                    <StyledTextField
                                        trueInputRef={inputEl}
                                        value={statementTemplate}
                                        onChange={() => {
                                            inputEl?.current && setStatementTemplate(inputEl?.current.value);
                                            setIsEdited(true);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {isEdited && <SaveButton buttonText={t("Settings.SaveChanges")} onSave={handleSavingSettings} />}
        </div>
    );
};
export default StatementSettings;
